import { useEffect, useContext, useState, useRef } from "react";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
// import Typography from "@mui/material/Typography";

import { APIContext } from "../../store/APIStore";
import IBadgeFormat from "../../dataProvider/interfaces/IBadgeFormat";
import { StoreContext } from "../../store/Store";
import { Loading, useRedirect } from "react-admin";
import { BiTrash, BiX, BiSearch, BiPlusCircle } from "react-icons/bi";
import GenericList, {
  dataType,
} from "../../components/GenericList/GenericList";
import { routePaths } from "../../baseLayout/SidebarMenu";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";
import { useHasPermissions } from "../../hooks/useHasPermissions";
import { PERMISSIONS } from "../../roles";
import FdButton from "../../components/UIComponents/Button/FdButton";
import FdTextField from "../../components/UIComponents/TextField/FdTextfield";
// import Search from "../../components/Search/Search";
import FdTypography from "../../components/UIComponents/Typography/FdTypography";
import { badgeSizeView as classes} from "./styles"

// Badge sizes page component
const BadgeSizesView = () => {
  const [state] = useContext(StoreContext);
  const api = useContext(APIContext);
  const [badgeFormats, setBadgeFormats] = useState<IBadgeFormat[]>();
  const [loading, setLoading] = useState(true);
  const [scrollLoading] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const redirect = useRedirect();
  const searchRef = useRef<any>(); // search bar
  const notificationRef = useRef<any>(); // notification bar

  // permissions
  const canManageBadgeSize = useHasPermissions(
    PERMISSIONS.CAN_MANAGE_BADGE_SIZE
  );

  // redirect if no permission to access this page
  useEffect(() => {
    if (canManageBadgeSize === false) {
      redirect(`/${routePaths.ACTIVE_EVENTS}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageBadgeSize]);

  // load data
  useEffect(() => {
    if (state.currentUser) {
      fetchBadgeFormats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentUser]);

  const fetchBadgeFormats = async (keyword = "") => {
    try {
      setLoading(true);
      const { data } = (await api.badgeFormatService.getBadgeFormats(
        state.currentUser?.id
      )) as any;

      if (keyword.length > 0) {
        const filteredFormats = data.filter(
          (format: IBadgeFormat) =>
            format.name &&
            format.name.toLowerCase().indexOf(keyword.toLowerCase()) >= 0
        );
        setBadgeFormats(filteredFormats);
      } else {
        setBadgeFormats(data);
      }

      setLoading(false);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleSearchClear = () => {
    redirect(`/${routePaths.BADGE_SIZES}`);
    setSearchKey("");
    fetchBadgeFormats();
  };

  const onSearchClick = (e: any) => {
    redirect(`/${routePaths.BADGE_SIZES}?search=${searchKey}`);
    fetchBadgeFormats(searchKey);
  };

  const handleSearchKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      redirect(`/${routePaths.BADGE_SIZES}?search=${searchKey}`);
      fetchBadgeFormats(searchKey);
    }
  };

  const handleDeleteBadgeFormat = async (id: number) => {
    try {
      await api.badgeFormatService.deleteBadgeFormat(id);
      fetchBadgeFormats();
      notificationRef.current?.show(
        "Badge size deleted",
        notificationType.Success
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleDuplicateItem = async (item: IBadgeFormat) => {
    try {
      const duplicatedBadgeFormat =
        await api.badgeFormatService.createBadgeFormat({
          name: `${item.name}-copy`,
          width: item.width,
          height: item.height,
          bleed: item.bleed,
          perforation: item.perforation,
          doubleSided: item.doubleSided,
          default: item.default,
        });
      redirect(
        `/${routePaths.BADGE_SIZES}/${duplicatedBadgeFormat.id}/${routePaths.EDIT}`
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const setDefaultItem = async (item: IBadgeFormat) => {
    try {
      setLoading(true);
      await api.badgeFormatService.updateBadgeFormat(item.id, {
        isDefault: true,
      });
      fetchBadgeFormats();
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleBulkDelete = async (items: number[]) => {
    try {
      await Promise.all(
        items.map((item) => {
          return api.badgeFormatService.deleteBadgeFormat(item);
        })
      );
      fetchBadgeFormats();
      notificationRef.current?.show(
        "Badge sizes deleted",
        notificationType.Success
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const bulkActions = [
    {
      label: "Delete",
      key: "delete",
      callback: handleBulkDelete,
      startIcon: <BiTrash />,
    },
  ];

  return (
    <>
      {!loading && (
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box
            sx={{
              height: "calc(100vh - 105px)",
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "10px",
              }}
            >
              <FdTypography label="Badge Sizes" component="span" variant="h3" />

              <FdButton
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<BiPlusCircle />}
                onClick={() => {
                  redirect(`/${routePaths.BADGE_SIZES}/new`);
                }}
                ButtonName="Add new size"
              />
            </Box>
            <Box sx={classes.topFilters} display="flex">
              {/* <Paper
                sx={classes.searchForm}
              > */}
              <FdTextField
                id="searchInput"
                className="searchInput"
                sx={classes.searchInput}
                placeholder="Search"
                type="text"
                inputRef={searchRef}
                name="search"
                onChange={(e) => {
                  const keyword = e.target.value;
                  setSearchKey(keyword);
                  // if (keyword.length === 0) {
                  //   setSearchKey("");
                  // }
                }}
                value={searchKey}
                autoFocus
                onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BiSearch size="18" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={classes.clearIcon}
                        onClick={handleSearchClear}
                      >
                        {searchKey.length > 0 && <BiX size="20" />}
                      </InputAdornment>
                    ),
                  }}
              />
              {/* </Paper> */}
              <FdButton
                variant="contained"
                color="primary"
                onClick={onSearchClick}
                sx={classes.searchButton}
                ButtonName="Search"
              />
            </Box>

            <Box sx={classes.listContent}>
              <GenericList
                type={dataType.BADGE_FORMATS}
                items={badgeFormats as IBadgeFormat[]}
                onBottomScroll={() => {}}
                onDeleteItem={handleDeleteBadgeFormat}
                onDuplicateItem={handleDuplicateItem}
                setDefault={(item: IBadgeFormat) => {
                  setDefaultItem(item);
                }}
                style={{ width: "100%" }}
                onSelectItem={(item: IBadgeFormat) => {
                  redirect(
                    `/${routePaths.BADGE_SIZES}/${item.id}/${routePaths.EDIT}`
                  );
                }}
                isLoading={scrollLoading}
                bulkActions={bulkActions}
              />
            </Box>
          </Box>
        </Box>
      )}

      {loading && <Loading />}

      <NotificationMessage ref={notificationRef} />
    </>
  );
};

export default BadgeSizesView;
